import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators'
import { IMenuItem, MenuItem } from '@template/classes/menu'
import FilterResponseModel from '~/shared/model/FilterResponseModel'
import { FilterModel } from '~/shared/model/FilterModel'
import { HierarchyModel } from '~/shared/model/HierarchyModel'
import { PageModel } from '~/shared/model/PageModel'
import ReportResponseModel from '~/shared/model/ReportResponseModel'
import { ReportModel } from '~/shared/model/ReportModel'
import { FiltertService } from '~/shared/service/filter.service'
import { ReportService } from '~/shared/service/report.service'
import { MainStore } from '~/store/index'
import { ActiveFilterModel } from '~/shared/model/ActiveFilterModel'
import { GroupedFilterModel } from '~/shared/model/GroupedFilterModel'
import { FilterValueModel } from '~/shared/model/FilterValueModel'
import { DefaultFilterResponseModel } from '~/shared/model/DefaultFilterResponseModel'

@Module({ namespaced: true, name: 'ReportModule', dynamic: true, store: MainStore })
export default class ReportModule extends VuexModule {
  reports: ReportModel[] = []
  filters: FilterModel[] = []
  activeFilters: GroupedFilterModel[] = []
  comtradeActiveFilters: GroupedFilterModel[] = []
  selectedPageName: string = 'Introduction'
  selectedReportName: string = 'SITA_Comext'
  showFilter: boolean = true
  menuItems: MenuItem[] = []

  get getSelectedPartnerIds(): string[] {
    return this.activeFilters.filter((p) => p.table === 'D_EU and not EU' && p.reportId === 1)[0]?.values.map((p) => p.value)
  }

  get getSelectedPartnerIdsComtrade(): string[] {
    return this.comtradeActiveFilters.filter((p) => p.table === 'D_EU not EU partner hierarchy')[0]?.values.map((p) => p.value)
  }

  get getSelectedComextProductIds(): string[] {
    return this.activeFilters.filter((p) => p.table === 'D_Trade products hierarchy')[0]?.values.map((p) => p.value)
  }

  get getSelectedComtradeProductIds(): string[] {
    return this.comtradeActiveFilters
      .filter((p) => p.table === 'D_Six digit GN trade codes hierarchy  including GN codes')[0]
      ?.values.map((p) => p.value)
  }

  get getSelectedDeclarantIds(): string[] {
    const test = this.activeFilters.filter((p) => p.table === 'D_Declarants')[0]?.values.map((p) => p.value)
    return test
  }

  get getSelectedReportIds(): string[] {
    const test = this.comtradeActiveFilters.filter((p) => p.table === 'D_Reporter')[0]?.values.map((p) => p.value)
    return test
  }

  get getSelectedPageName(): string {
    return this.selectedPageName
  }

  get getShowFilter(): boolean {
    return this.showFilter
  }

  get getMenuItems(): MenuItem[] {
    return this.menuItems
  }

  get getSelectedReportName(): string {
    return this.selectedReportName
  }

  @Mutation
  SetMenuItems() {
    const subItems: MenuItem[] = []

    this.menuItems.length = 0

    const report = this.reports.find((p) => p.name === this.selectedReportName)
    this.menuItems.push(
      new MenuItem({
        title: 'Start',
        subTitle: 'Go from here',
        disabled: false,
        icon: 'mdi-google-circles-communities',
        location: {
          name: 'index-introduction',
        },
      })
    )

    report?.pages.forEach((page) => {
      this.menuItems.push(
        new MenuItem({
          title: page.name,
          icon: page.icon,
          color: 'success',
          location: {
            name: 'index-page',
            params: {
              page: page.name,
            },
          },
        })
      )
    })

    this.menuItems.push(
      new MenuItem({
        title: 'Disclaimer',
        icon: 'mdi-hand-back-left',
        color: 'success',
        location: {
          name: 'index-disclaimer',
        },
      })
    )
    this.menuItems.push(
      new MenuItem({
        title: 'Contact',
        icon: 'mdi-handshake',
        color: 'success',
        location: {
          name: 'index-contact',
        },
      })
    )
  }

  @Mutation
  SetSelectedReport(reportName: string) {
    this.selectedReportName = reportName
  }

  @Mutation
  SetShowFilter(pageName: string): boolean {
    switch (pageName) {
      case '/contact':
      case '/contact/':
      case '/disclaimer':
      case '/disclaimer/':
      case '/introduction':
      case '/introduction/':
        this.showFilter = false
        return false
      default:
        this.showFilter = true
        return true
    }
  }

  @Mutation
  SetSelectedPageName(pageName: string): void {
    this.selectedPageName = pageName
  }

  @Mutation
  addDefaultFiltersToStore(defaultFilters: DefaultFilterResponseModel[]) {
    defaultFilters.forEach((defFilter) => {
      if (defFilter.reportId === 2) {
        this.comtradeActiveFilters.push(
          new GroupedFilterModel({
            table: defFilter.table,
            column: defFilter.column,
            operator: defFilter.operator,
            values: defFilter.values,
            reportId: defFilter.reportId,
          })
        )
      }
      if (defFilter.reportId === 1) {
        this.activeFilters.push(
          new GroupedFilterModel({
            table: defFilter.table,
            column: defFilter.column,
            operator: defFilter.operator,
            values: defFilter.values,
            reportId: defFilter.reportId,
          })
        )
      }
    })
  }

  @Mutation
  addFiltersToStore(filters: FilterResponseModel[]) {
    this.filters = [
      ...filters.map((rep) => {
        return new FilterModel({
          name: rep.name,
          displayName: rep.displayName,
          filtervisual: rep.filterVisual,
          operation: rep.operation,
          table: rep.tabel,
          reportId: rep.reportId,
          defaultValue: rep.defaultValue,
          values: rep.values.map((valueRep) => {
            return new HierarchyModel({
              value: valueRep.value,
              anotherLevel: valueRep.anotherLevel,
              columnName: valueRep.columnName,
              valueId: valueRep.valueId,
              levels: valueRep.level.map((level1) => {
                return new HierarchyModel({
                  value: level1.value,
                  anotherLevel: level1.anotherLevel,
                  columnName: level1.columnName,
                  valueId: level1.valueId,
                  levels: level1.level.map((level2) => {
                    return new HierarchyModel({
                      value: level2.value,
                      anotherLevel: level2.anotherLevel,
                      columnName: level2.columnName,
                      valueId: level2.valueId,
                      levels: level2.level.map((level3) => {
                        return new HierarchyModel({
                          value: level3.value,
                          anotherLevel: level3.anotherLevel,
                          columnName: level3.columnName,
                          valueId: level3.valueId,
                          levels: level3.level.map((level4) => {
                            return new HierarchyModel({
                              value: level4.value,
                              anotherLevel: level4.anotherLevel,
                              columnName: level4.columnName,
                              valueId: level4.valueId,
                              levels: level4.level.map((level5) => {
                                return new HierarchyModel({
                                  value: level5.value,
                                  anotherLevel: level5.anotherLevel,
                                  columnName: level5.columnName,
                                  valueId: level5.valueId,
                                  levels: level5.level.map((level6) => {
                                    return new HierarchyModel({
                                      value: level6.value,
                                      anotherLevel: level6.anotherLevel,
                                      columnName: level6.columnName,
                                      valueId: level6.valueId,
                                      levels: level6.level.map((level7) => {
                                        return new HierarchyModel({
                                          value: level7.value,
                                          anotherLevel: level7.anotherLevel,
                                          columnName: level7.columnName,
                                          valueId: level7.valueId,
                                          levels: level7.level.map((level8) => {
                                            return new HierarchyModel({
                                              value: level8.value,
                                              anotherLevel: level8.anotherLevel,
                                              columnName: level8.columnName,
                                              valueId: level8.valueId,
                                              levels: level8.level.map((level9) => {
                                                return new HierarchyModel({
                                                  value: level9.value,
                                                  anotherLevel: level9.anotherLevel,
                                                  columnName: level9.columnName,
                                                  valueId: level9.valueId,
                                                })
                                              }),
                                            })
                                          }),
                                        })
                                      }),
                                    })
                                  }),
                                })
                              }),
                            })
                          }),
                        })
                      }),
                    })
                  }),
                })
              }),
            })
          }),
        })
      }),
    ]
  }

  @Mutation
  addReportsAndPagesToStore(reportLijst: ReportResponseModel[]) {
    this.reports = [
      ...reportLijst.map((rep) => {
        return new ReportModel({
          name: rep.name,
          description: rep.description,
          pages: rep.pages.map((page) => {
            return new PageModel({
              name: page.name,
              active: page.active,
              icon: page.icon,
              order: page.order,
            })
          }),
        })
      }),
    ]
  }

  @Mutation
  updateActiveFilterForRadioButton(filter: ActiveFilterModel) {
    const columnExists = this.activeFilters.filter((p) => p.table === filter.table && p.column === filter.column)
    const columnExistsInComtrade = this.comtradeActiveFilters.filter((p) => p.table === filter.table && p.column === filter.column)

    if (columnExistsInComtrade.length > 0 && filter.reportId === 2) {
      // delete if exists
      const index = this.comtradeActiveFilters.indexOf(columnExistsInComtrade[0])
      this.comtradeActiveFilters.splice(index, 1)
    }
    if (columnExists.length > 0 && filter.reportId === 1) {
      // delete if exists
      const index = this.activeFilters.indexOf(columnExists[0])
      this.activeFilters.splice(index, 1)
    }
    if (filter.table === 'MetaData Parameters' && filter.reportId === 1) {
      // add new filter
      this.activeFilters.push(
        new GroupedFilterModel({
          column: filter.column,
          operator: filter.operator,
          table: filter.table,
          reportId: filter.reportId,
          values: [new FilterValueModel({ value: filter.value })],
        })
      )
    }
    if (filter.table === 'MetaData Parameters' && filter.reportId === 2) {
      // add new filter
      this.comtradeActiveFilters.push(
        new GroupedFilterModel({
          column: filter.column,
          operator: filter.operator,
          table: filter.table,
          reportId: filter.reportId,
          values: [new FilterValueModel({ value: filter.value })],
        })
      )
    }

    if (filter.table === 'D_Flow' && filter.reportId === 1) {
      // add new filter
      this.activeFilters.push(
        new GroupedFilterModel({
          column: filter.column,
          operator: filter.operator,
          table: filter.table,
          reportId: filter.reportId,
          values: [new FilterValueModel({ value: filter.value })],
        })
      )
    }
    if (filter.table === 'D_Flow' && filter.reportId === 2) {
      // add new filter
      this.comtradeActiveFilters.push(
        new GroupedFilterModel({
          column: filter.column,
          operator: filter.operator,
          table: filter.table,
          reportId: filter.reportId,
          values: [new FilterValueModel({ value: filter.value })],
        })
      )
    }
    if (filter.table === 'D_Declarants' && filter.reportId === 1) {
      // add new filter
      this.activeFilters.push(
        new GroupedFilterModel({
          column: filter.column,
          operator: filter.operator,
          table: filter.table,
          reportId: filter.reportId,
          values: [new FilterValueModel({ value: filter.value })],
        })
      )
    }
    if (filter.table === 'D_Reporter' && filter.reportId === 2) {
      // add new filter
      this.comtradeActiveFilters.push(
        new GroupedFilterModel({
          column: filter.column,
          operator: filter.operator,
          table: filter.table,
          reportId: filter.reportId,
          values: [new FilterValueModel({ value: filter.value })],
        })
      )
    }
  }

  @Mutation
  updateCombinedPartnerHierarchiesSelection(selectie: HierarchyModel[]) {
    const selectedTable = this.activeFilters.filter((p) => p.table === 'D_EU and not EU' && p.reportId === 1)
    // Add filter group + Value
    if (selectedTable.length < 1) {
      this.activeFilters.push(
        new GroupedFilterModel({
          column: 'Level3',
          operator: 'IN',
          table: 'D_EU and not EU',
          reportId: 1,
          values: [],
        })
      )

      const selectedNewTable = this.activeFilters.filter((p) => p.table === 'D_EU and not EU' && p.reportId === 1)
      selectie.forEach((selectedValue) => {
        selectedNewTable[0].values.push(
          new FilterValueModel({
            id: selectedValue.valueId,
            value: selectedValue.value,
          })
        )
      })
    }
    // Add value
    else {
      selectedTable[0].values.splice(0, selectedTable[0].values.length)
      selectie.forEach((selectedValue) => {
        selectedTable[0].values.push(
          new FilterValueModel({
            id: selectedValue.valueId,
            value: selectedValue.value,
          })
        )
      })
    }
  }

  @Mutation
  updateEUNotEUPartnerHierarchySelection(selectie: HierarchyModel[]) {
    const selectedTable = this.comtradeActiveFilters.filter((p) => p.table === 'D_EU not EU partner hierarchy' && p.reportId === 2)
    // Add filter group + Value
    if (selectedTable.length < 1) {
      this.comtradeActiveFilters.push(
        new GroupedFilterModel({
          column: 'Level4',
          operator: 'IN',
          table: 'D_EU not EU partner hierarchy',
          reportId: 2,
          values: [],
        })
      )

      const selectedNewTable = this.comtradeActiveFilters.filter((p) => p.table === 'D_EU not EU partner hierarchy' && p.reportId === 2)
      selectie.forEach((selectedValue) => {
        selectedNewTable[0].values.push(
          new FilterValueModel({
            id: selectedValue.valueId,
            value: selectedValue.value,
          })
        )
      })
    }
    // Add value
    else {
      selectedTable[0].values.splice(0, selectedTable[0].values.length)
      selectie.forEach((selectedValue) => {
        selectedTable[0].values.push(
          new FilterValueModel({
            id: selectedValue.valueId,
            value: selectedValue.value,
          })
        )
      })
    }
  }

  @Mutation updateComextProductSelection(selectie: HierarchyModel[]) {
    const selectedTable = this.activeFilters.filter((p) => p.table === 'D_Trade products hierarchy')
    // Add filter group + Value
    if (selectedTable.length < 1) {
      this.activeFilters.push(
        new GroupedFilterModel({
          column: 'Level8',
          operator: 'IN',
          table: 'D_Trade products hierarchy',
          values: [],
        })
      )

      const selectedNewTable = this.activeFilters.filter((p) => p.table === 'D_Trade products hierarchy')
      selectie.forEach((selectedValue) => {
        selectedNewTable[0].values.push(
          new FilterValueModel({
            id: selectedValue.valueId,
            value: selectedValue.value,
          })
        )
      })
    }
    // Add value
    else {
      selectedTable[0].values.splice(0, selectedTable[0].values.length)
      selectie.forEach((selectedValue) => {
        selectedTable[0].values.push(
          new FilterValueModel({
            id: selectedValue.valueId,
            value: selectedValue.value,
          })
        )
      })
    }
  }

  @Mutation
  updateComtradeProductSelection(selectie: HierarchyModel[]) {
    const selectedTable = this.comtradeActiveFilters.filter((p) => p.table === 'D_Six digit GN trade codes hierarchy  including GN codes')
    // Add filter group + Value
    if (selectedTable.length < 1) {
      this.comtradeActiveFilters.push(
        new GroupedFilterModel({
          column: 'Level5',
          operator: 'IN',
          table: 'D_Six digit GN trade codes hierarchy  including GN codes',
          values: [],
        })
      )

      const selectedNewTable = this.comtradeActiveFilters.filter(
        (p) => p.table === 'D_Six digit GN trade codes hierarchy  including GN codes'
      )
      selectie.forEach((selectedValue) => {
        selectedNewTable[0].values.push(
          new FilterValueModel({
            id: selectedValue.valueId,
            value: selectedValue.value,
          })
        )
      })
    }
    // Add value
    else {
      selectedTable[0].values.splice(0, selectedTable[0].values.length)
      selectie.forEach((selectedValue) => {
        selectedTable[0].values.push(
          new FilterValueModel({
            id: selectedValue.valueId,
            value: selectedValue.value,
          })
        )
      })
    }
  }

  @Mutation
  updateDimensionsCOMEXTDeclarantSelection(selectie: HierarchyModel[]) {
    const selectedTable = this.activeFilters.filter((p) => p.table === 'D_Declarants')

    // Add filter group + Value
    if (selectedTable.length < 1) {
      this.activeFilters.push(
        new GroupedFilterModel({
          column: 'Name',
          operator: 'IN',
          table: 'D_Declarants',
          values: [],
        })
      )

      const selectedNewTable = this.activeFilters.filter((p) => p.table === 'D_Declarants')
      selectie.forEach((selectedValue) => {
        selectedNewTable[0].values.push(
          new FilterValueModel({
            id: selectedValue.valueId,
            value: selectedValue.value,
          })
        )
      })
    }
    // Add value
    else {
      selectedTable[0].values.splice(0, selectedTable[0].values.length)
      selectie.forEach((selectedValue) => {
        selectedTable[0].values.push(
          new FilterValueModel({
            id: selectedValue.valueId,
            value: selectedValue.value,
          })
        )
      })
    }
  }

  @Mutation
  updateDimensionsReporterSelection(selectie: HierarchyModel[]) {
    const selectedTable = this.comtradeActiveFilters.filter((p) => p.table === 'D_Reporter')

    // Add filter group + Value
    if (selectedTable.length < 1) {
      this.comtradeActiveFilters.push(
        new GroupedFilterModel({
          column: 'name',
          operator: 'IN',
          table: 'D_Reporter',
          values: [],
        })
      )

      const selectedNewTable = this.comtradeActiveFilters.filter((p) => p.table === 'D_Reporter')
      selectie.forEach((selectedValue) => {
        selectedNewTable[0].values.push(
          new FilterValueModel({
            id: selectedValue.valueId,
            value: selectedValue.value,
          })
        )
      })
    }
    // Add value
    else {
      selectedTable[0].values.splice(0, selectedTable[0].values.length)
      selectie.forEach((selectedValue) => {
        selectedTable[0].values.push(
          new FilterValueModel({
            id: selectedValue.valueId,
            value: selectedValue.value,
          })
        )
      })
    }
  }

  @Action
  async getReportsAndPages(): Promise<void> {
    await ReportService.GetReports().then((resp) => {
      this.addReportsAndPagesToStore(resp)
    })
  }

  @Action
  async getFilters(reportName: string): Promise<void> {
    await FiltertService.GetFilters(reportName).then((resp) => {
      this.addFiltersToStore(resp)
    })
  }

  @Action
  async getDefaultFilterValues(reportName: string): Promise<void> {
    await FiltertService.GetDefaultFilters().then((resp) => {
      this.addDefaultFiltersToStore(resp)
    })
  }
}
